import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";

import ECGHeart from "../../icons/ECGHeart";
import Spacer from "../../Spacer";
import { formatValidatedMeasurement } from "../../../utils/results/validatedRanges";
import { SignalChart } from "../../charts/SignalChart";
import PoincareChart from "../../charts/PoincareChart";
import PostExertionDecayChart from "../../charts/PostExertionDecayChart";
import FlowVolumeLoopsSection from "../FlowVolumeLoopsSection";
import { SHOW_FULL_RESULTS } from "../../../config/results";
import { formatRecordingIdDateParts } from "../../../utils/results/recordingIdTime";
import SignalQuality from "../../icons/SignalQuality";
import LungIcon from "../../icons/LungIcon";
import {
  ResultSummaryRow,
  ResultSummarySection,
} from "../ResultSummarySection";
import { trackEvent } from "../../../utils/analytics/track";
import { pickRespiratoryObservedRrSeries } from "../../../utils/results/respiratoryPoincare";
import ResultQualityNotice from "../ResultQualityNotice";
import ResultViewScaffold from "../ResultViewScaffold";
import {
  buildPoincareFromIntervals,
  hasFirestoreFlowLoops,
  pickCardiopulmonaryDetrendedSignal,
  pickHeartDxPeakIntervals,
} from "../../../utils/results/cardiopulmonaryView";
import {
  formatPassFailLabel,
  getHeartRateQualityMessage,
  getRespiratoryQualityMessage,
} from "../../../utils/results/resultQuality";
import {
  isPassingHrSignalQuality,
  pickHrSignalQuality,
  pickRr,
  pickRrSignalQuality,
} from "../../../utils/results/signalQuality";
import {
  pickHeartDxHrBpm,
  pickHeartDxHRV,
} from "../../../utils/results/heartdx";
import { isCardiopulmonaryCompleteDoc } from "../../../utils/results/cardiopulmonaryStatus";

function CardiopulmonaryResultViewImpl({ data, docId, onFinish }) {
  const { t, i18n } = useTranslation();
  console.log(data);
  const rr = useMemo(() => pickRr(data), [data]);
  const rrSignalQuality = useMemo(() => pickRrSignalQuality(data), [data]);
  const hr = useMemo(() => pickHeartDxHrBpm(data), [data]);
  const hrv = useMemo(() => pickHeartDxHRV(data), [data]);

  const hrQuality = useMemo(() => pickHrSignalQuality(data), [data]);

  const rrMsg = useMemo(
    () => getRespiratoryQualityMessage(rrSignalQuality, t),
    [rrSignalQuality, t],
  );
  const hrMsg = useMemo(
    () => getHeartRateQualityMessage(hrQuality, isPassingHrSignalQuality, t),
    [hrQuality, t],
  );

  const cardiacQualityIsFailing = Boolean(hrMsg);
  const respiratoryQualityIsFailing = Boolean(rrMsg);
  const recordingQualityIsFailing =
    cardiacQualityIsFailing || respiratoryQualityIsFailing;

  const cardiacQualityLabel = useMemo(
    () => formatPassFailLabel(cardiacQualityIsFailing, t),
    [cardiacQualityIsFailing, t],
  );

  const respiratoryQualityLabel = useMemo(
    () => formatPassFailLabel(respiratoryQualityIsFailing, t),
    [respiratoryQualityIsFailing, t],
  );

  const rrDisplay = useMemo(() => formatValidatedMeasurement("rr", rr), [rr]);
  const hrDisplay = useMemo(() => formatValidatedMeasurement("hr", hr), [hr]);

  const hrvDisplay = useMemo(
    () => formatValidatedMeasurement("hrv", hrv),
    [hrv],
  );

  const peakIntervals = useMemo(() => pickHeartDxPeakIntervals(data), [data]);

  const poincare = useMemo(
    () => buildPoincareFromIntervals(peakIntervals),
    [peakIntervals],
  );

  const mpmDetrendedSignal = useMemo(
    () => pickCardiopulmonaryDetrendedSignal(data),
    [data],
  );

  const respiratoryObservedRrSeries = useMemo(
    () => pickRespiratoryObservedRrSeries(data),
    [data],
  );

  const recordingTime = useMemo(
    () =>
      formatRecordingIdDateParts(
        docId,
        i18n.resolvedLanguage || i18n.language || "en-GB",
      ),
    [docId, i18n.resolvedLanguage, i18n.language],
  );

  const details = SHOW_FULL_RESULTS ? (
    <>
      <section className="p-4 pt-2">
        <h2 className="subtitle-text">
          {t("result.pulse.poincare", "Poincaré")}
        </h2>
        {poincare.x.length && poincare.y.length ? (
          <PoincareChart
            x={poincare.x}
            y={poincare.y}
            height={280}
            initialMode="basic"
          />
        ) : (
          <div className="muted-text text-center">
            {t("result.pulse.noPoincare", "No Poincaré data available.")}
          </div>
        )}
      </section>

      <section className="p-4 pt-2">
        <h2 className="subtitle-text">
          {t("result.resp.signalChartTitle", "Respiratory Signal")}
        </h2>

        {mpmDetrendedSignal.points.length ? (
          <SignalChart
            smoothSignal={mpmDetrendedSignal.points}
            rawSignal={[]}
            peaks={[]}
            troughs={[]}
            height={220}
            maxSecs={mpmDetrendedSignal.maxSecs}
          />
        ) : (
          <div className="muted-text">
            {t("result.resp.noSignal", "No signal trace available.")}
          </div>
        )}
      </section>

      {respiratoryObservedRrSeries.length ? (
        <section className="p-4 pt-2">
          <h2 className="subtitle-text">
            {t("result.resp.rrPerBreathHeading", "Respiratory Rate per Breath")}
          </h2>

          <PostExertionDecayChart
            points={respiratoryObservedRrSeries}
            height={260}
            showFits={false}
          />
        </section>
      ) : null}

      {/* <section className="p-4 pt-2">
            <h2 className="subtitle-text">
              {t(
                "result.resp.breathingVariabilityPoincareTitle",
                "Breathing Variability Poincare",
              )}
            </h2>

            {respiratoryPoincare.x.length && respiratoryPoincare.y.length ? (
              <PoincareChart
                x={respiratoryPoincare.x}
                y={respiratoryPoincare.y}
                height={280}
                initialMode="basic"
                axisMin={0}
                axisMax={respiratoryPoincareAxisMax}
                xTitle={t("result.resp.breathingVariabilityXAxis", "IBI_n (s)")}
                yTitle={t("result.resp.breathingVariabilityYAxis", "IBI_n+1 (s)")}
              />
            ) : (
              <div className="muted-text text-center">
                {t(
                  "result.resp.noBreathingVariabilityPoincare",
                  "No breathing variability Poincare data available.",
                )}
              </div>
            )}
          </section> */}

      <section className="p-4 pt-2">
        <div className="w-full text-left">
          {hasFirestoreFlowLoops(data) ? (
            <FlowVolumeLoopsSection
              recordJson={data?.results?.mpm?.flow_loop}
              hideSpirometryManeuverBreaths={false}
              showOverlay={true}
              showMetricBar={false}
              showMetricsTable={false}
            />
          ) : (
            <div className="muted-text">
              {t(
                "result.spiro.noFlowLoops",
                "No additional flow-loop data available.",
              )}
            </div>
          )}
        </div>
      </section>
    </>
  ) : null;

  return (
    <ResultViewScaffold
      title={t(
        "result.cardiopulmonary.completeTitle",
        "Cardiopulmonary Scan Complete",
      )}
      recordingTime={recordingTime}
      onFinish={onFinish}
      details={details}
    >
      <Spacer />

      <ResultSummarySection
        icon={<SignalQuality />}
        title={t("result.qualityTitle", "Recording Quality")}
        disabled={recordingQualityIsFailing}
      >
        <ResultSummaryRow
          label={t("result.cardiopulmonary.cardiacQuality", "Cardiac Quality")}
          value={cardiacQualityLabel}
          disabled={cardiacQualityIsFailing}
        />

        <ResultSummaryRow
          label={t(
            "result.cardiopulmonary.respiratoryQuality",
            "Respiratory Quality",
          )}
          value={respiratoryQualityLabel}
          disabled={respiratoryQualityIsFailing}
        />

        <ResultQualityNotice
          messages={[hrMsg, rrMsg]}
          linkTo="/guidance/cardiopulmonary"
          ctaLabel={t("result.cardiopulmonary.guidanceCta", "Recording Tips")}
          onLinkClick={() =>
            trackEvent("cardiopulmonary_result_click_guidance", {
              cardiac_quality_failed: cardiacQualityIsFailing,
              respiratory_quality_failed: respiratoryQualityIsFailing,
            })
          }
        />
      </ResultSummarySection>

      {SHOW_FULL_RESULTS ? (
        <>
          <Spacer />
          <ResultSummarySection
            icon={<ECGHeart />}
            title={t("result.pulse.cardiac", "Cardiac")}
            disabled={Boolean(hrMsg)}
          >
            <ResultSummaryRow
              label={t("result.pulse.hrLabel", "Heart Rate")}
              value={hrDisplay}
              unit={t("result.pulse.unit", "bpm")}
              disabled={Boolean(hrMsg)}
            />
            <ResultSummaryRow
              label={t("result.pulse.hrvLabel", "HRV")}
              value={hrvDisplay}
              unit={t("result.pulse.hrvUnit", "ms")}
              disabled={Boolean(hrMsg)}
            />
          </ResultSummarySection>
          <Spacer />
        </>
      ) : null}

      {SHOW_FULL_RESULTS ? (
        <ResultSummarySection
          icon={<LungIcon />}
          title={t("result.tidal.respiratory", "Respiratory")}
          disabled={Boolean(rrMsg)}
        >
          <ResultSummaryRow
            label={t("result.resp.rrLongLabel", "Respiratory Rate")}
            value={rrDisplay}
            unit={t("result.resp.rrUnit", "bpm")}
            disabled={Boolean(rrMsg)}
          />
        </ResultSummarySection>
      ) : null}
    </ResultViewScaffold>
  );
}

const CardiopulmonaryResultView = memo(CardiopulmonaryResultViewImpl);

CardiopulmonaryResultView.isReady = (data) =>
  isCardiopulmonaryCompleteDoc(data) &&
  (pickRr(data) != null || pickHeartDxHrBpm(data) != null);

export default CardiopulmonaryResultView;
